import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowClockwise, Tag, Warning } from 'shared/icons';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import { Mono, Sku } from '../ui/Mono';
import { Skeleton } from '../ui/Skeleton';
import { useToast } from '../ui/Toast';
import api from '../../services/api';
import { FormatInt } from '../../utils/format';

// ─────────────────────────────────────────────────────────────────────────────
// GERAR LOTE & OS — o carrinho. TUDO entidade do melidash (o zpl-checkout não
// é tocado: a ideia é SUBSTITUÍ-lo, nunca integrar).
//
// A demanda é POR SKU, sempre: a empresa NUNCA faz envio multi — pedido
// multi-SKU/multi-volume é anomalia que se resolve na aba Demandas (split
// antes da NF) e por isso nem aparece aqui. O dono seleciona SKUs, ajusta as
// quantidades se quiser, e escolhe o destino: LOTE (impressão conjugada —
// único modo que existe) ou OS DE PICKING (que alimenta as abas Picking e
// Packing desta mesma página).
// ─────────────────────────────────────────────────────────────────────────────

// Os mesmos tetos do backend (que é quem manda): 100 etiquetas por lote — o
// número do zpl-checkout — e 50 unidades por OS de picking, o que cabe num
// carrinho. Aqui eles servem para AVISAR antes de o operador montar o carrinho
// inteiro e levar erro na cara.
const LOTE_MAX_ETIQUETAS = 100;
const OS_PICKING_MAX_UNIDADES = 50;

const CarrinhoGeracao = ({ podeOperar = false, onGerou }) => {
    const toast = useToast();
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [selecao, setSelecao] = useState(() => new Map()); // sku -> {row, qty}
    const [rotulo, setRotulo] = useState('');
    const [observacao, setObservacao] = useState('');
    const [gerando, setGerando] = useState(null); // 'lote' | 'os' | null
    // Traz de volta os envios que o zpl-checkout JÁ imprimiu. Fica DESLIGADO
    // por padrão — ligado sem querer, a mesma etiqueta sai duas vezes e o
    // pacote vai com a errada. Só faz sentido quando a pilha impressa no
    // sistema antigo vai ser descartada.
    const [incluirLegado, setIncluirLegado] = useState(false);
    const [resultado, setResultado] = useState(null);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const resposta = await api.getColetasCarrinhoDemandas();
            setDados(resposta);
            setSelecao((atual) => {
                const proximo = new Map();
                for (const linha of resposta?.results ?? []) {
                    if (atual.has(linha.sku)) {
                        const anterior = atual.get(linha.sku);
                        proximo.set(linha.sku, { row: linha, qty: Math.min(anterior.qty, linha.envios) });
                    }
                }
                return proximo;
            });
        } catch (error) {
            toast.error(error?.message || 'Não foi possível carregar a demanda.');
        } finally {
            setCarregando(false);
        }
    }, [toast]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const alternar = (linha) => {
        setSelecao((atual) => {
            const proximo = new Map(atual);
            if (proximo.has(linha.sku)) {
                proximo.delete(linha.sku);
            } else {
                proximo.set(linha.sku, { row: linha, qty: linha.envios });
            }
            return proximo;
        });
    };

    const ajustarQty = (sku, valor) => {
        setSelecao((atual) => {
            const proximo = new Map(atual);
            const item = proximo.get(sku);
            if (item) {
                proximo.set(sku, { ...item, qty: Math.max(1, Math.min(Number(valor) || 1, item.row.envios)) });
            }
            return proximo;
        });
    };

    const escolhidos = useMemo(() => [...selecao.values()], [selecao]);
    const totalQty = escolhidos.reduce((soma, item) => soma + item.qty, 0);

    const gerar = async (destino) => {
        setGerando(destino);
        try {
            const itens = escolhidos.map((item) => ({ sku: item.row.sku, qty: item.qty }));
            const resposta =
                destino === 'lote'
                    ? await api.postColetasCarrinhoLote({
                          itens,
                          ...(rotulo.trim() ? { rotulo: rotulo.trim() } : {}),
                          ...(incluirLegado ? { incluir_legado: true } : {}),
                      })
                    : await api.postColetasCarrinhoOs({
                          itens,
                          ...(observacao.trim() ? { observacao: observacao.trim() } : {}),
                          ...(incluirLegado ? { incluir_legado: true } : {}),
                      });
            setResultado({ tipo: destino, dados: resposta });
            setSelecao(new Map());
            setRotulo('');
            setObservacao('');
            await carregar();
            onGerou?.();
        } catch (error) {
            toast.error(error?.message || 'Não foi possível gerar.');
        } finally {
            setGerando(null);
        }
    };

    if (carregando && !dados) {
        return <Skeleton className="h-80 w-full" />;
    }

    if (dados && dados.habilitado === false) {
        return (
            <div className="rounded-xl border border-warning-200 bg-warning-soft p-4 text-sm text-warning-text dark:border-warning-900 dark:bg-warning-900/20">
                <Warning size={16} className="mr-1.5 inline" />
                {dados.motivo}
            </div>
        );
    }

    const linhas = dados?.results ?? [];

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="max-w-[72ch] text-sm text-mono-600 dark:text-mono-400">
                    Demanda <strong>por SKU</strong> dos envios livres (1 SKU × 1 unidade — a empresa não envia
                    multi; pedido multi se resolve na aba <strong>Demandas</strong>, com split antes da NF).
                    Selecione, ajuste as quantidades e escolha o destino: <strong>Lote</strong> (sempre conjugada)
                    ou <strong>OS de picking</strong>.
                </p>
                <Button variant="secondary" onClick={carregar} disabled={carregando}>
                    <ArrowClockwise size={16} className="mr-1.5" />
                    Atualizar demanda
                </Button>
            </div>

            {/* ── Carrinho ── */}
            <div className="rounded-xl border border-mono-200 bg-mono-50 p-4 dark:border-mono-800 dark:bg-mono-900">
                {escolhidos.length === 0 ? (
                    <p className="text-sm text-mono-500 dark:text-mono-400">
                        Carrinho vazio — marque SKUs na tabela abaixo.
                    </p>
                ) : (
                    <div className="space-y-3">
                        <Badge variant="info">
                            {FormatInt(escolhidos.length)} SKU(s) · {FormatInt(totalQty)} envio(s)
                        </Badge>
                        <ul className="space-y-1.5">
                            {escolhidos.map((item) => (
                                <li key={item.row.sku} className="flex flex-wrap items-center gap-2 text-sm">
                                    <Sku value={item.row.sku} className="text-xs" />
                                    <span className="max-w-[24rem] truncate text-xs text-mono-500" title={item.row.titulo}>
                                        {item.row.titulo}
                                    </span>
                                    <label className="ml-auto flex items-center gap-1 text-xs text-mono-500">
                                        qty
                                        <input
                                            type="number"
                                            min={1}
                                            max={item.row.envios}
                                            value={item.qty}
                                            onChange={(evento) => ajustarQty(item.row.sku, evento.target.value)}
                                            className="w-16 rounded border border-mono-300 bg-white px-1.5 py-0.5 text-right text-xs dark:border-mono-700 dark:bg-mono-950 dark:text-white"
                                        />
                                        <span className="text-mono-400">/ {FormatInt(item.row.envios)}</span>
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => alternar(item.row)}
                                        className="text-xs text-danger-text hover:underline"
                                    >
                                        remover
                                    </button>
                                </li>
                            ))}
                        </ul>
                        {/* A porta para refazer o que o sistema antigo imprimiu.
                            Fica FORA da linha dos botões, com o aviso junto: é
                            decisão de descartar papel impresso, não um filtro. */}
                        <label className="flex items-start gap-2 border-t border-mono-200 pt-3 text-sm dark:border-mono-800">
                            <input
                                type="checkbox"
                                checked={incluirLegado}
                                onChange={(evento) => setIncluirLegado(evento.target.checked)}
                                className="mt-0.5 h-4 w-4"
                            />
                            <span>
                                <span className="font-semibold text-black dark:text-white">
                                    Incluir os que o sistema antigo já imprimiu
                                </span>
                                <span className="block text-xs text-mono-600 dark:text-mono-400">
                                    Só marque se as etiquetas impressas no zpl-checkout forem DESCARTADAS — senão a
                                    mesma etiqueta sai duas vezes.
                                </span>
                            </span>
                        </label>
                        <div className="flex flex-wrap items-end gap-2 border-t border-mono-200 pt-3 dark:border-mono-800">
                            <div className="min-w-[200px] flex-1">
                                <Input
                                    label="Rótulo do lote (opcional)"
                                    value={rotulo}
                                    onChange={(evento) => setRotulo(evento.target.value)}
                                    placeholder="ex.: Figurinhas manhã"
                                />
                            </div>
                            <Button
                                variant="primary"
                                onClick={() => gerar('lote')}
                                disabled={!podeOperar || gerando !== null || totalQty > LOTE_MAX_ETIQUETAS}
                                title={
                                    !podeOperar
                                        ? 'Requer wms:operate'
                                        : totalQty > LOTE_MAX_ETIQUETAS
                                          ? `Um lote leva no máximo ${LOTE_MAX_ETIQUETAS} etiquetas`
                                          : 'Gera o lote (conjugada) no melidash'
                                }
                            >
                                <Tag size={16} className="mr-1.5" />
                                {gerando === 'lote' ? 'Gerando…' : `Gerar Lote (${FormatInt(totalQty)})`}
                            </Button>
                            <div className="min-w-[200px] flex-1">
                                <Input
                                    label="Observação da OS (opcional)"
                                    value={observacao}
                                    onChange={(evento) => setObservacao(evento.target.value)}
                                    placeholder="ex.: pallet rações"
                                />
                            </div>
                            <Button
                                variant="secondary"
                                onClick={() => gerar('os')}
                                disabled={!podeOperar || gerando !== null || totalQty > OS_PICKING_MAX_UNIDADES}
                                title={!podeOperar ? 'Requer wms:operate' : totalQty > OS_PICKING_MAX_UNIDADES ? `Uma OS de picking leva no máximo ${OS_PICKING_MAX_UNIDADES} unidades` : 'Gera a OS de picking no melidash'}
                            >
                                {gerando === 'os' ? 'Gerando…' : `Gerar OS de picking (${FormatInt(totalQty)})`}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Demanda por SKU ── */}
            <div className="overflow-hidden rounded-xl border border-mono-200 dark:border-mono-800">
                <table className="w-full text-sm">
                    <thead className="bg-mono-50 dark:bg-mono-900">
                        <tr className="text-left text-xs uppercase tracking-wide text-mono-500">
                            <th className="w-10 px-3 py-2" aria-label="Seleção" />
                            <th className="whitespace-nowrap px-3 py-2 font-medium">SKU</th>
                            <th className="w-full max-w-0 px-3 py-2 font-medium">Produto</th>
                            <th className="whitespace-nowrap px-3 py-2 text-right font-medium">Envios</th>
                            <th className="whitespace-nowrap px-3 py-2 text-right font-medium">Atrasados</th>
                            <th className="whitespace-nowrap px-3 py-2 text-right font-medium">SLA hoje</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-mono-100 dark:divide-mono-900">
                        {linhas.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-3 py-6 text-center text-sm text-mono-500">
                                    Nenhum envio livre no momento.
                                </td>
                            </tr>
                        ) : (
                            linhas.map((linha) => {
                                const marcado = selecao.has(linha.sku);
                                return (
                                    <tr
                                        key={linha.sku}
                                        onClick={() => alternar(linha)}
                                        className={`cursor-pointer transition-colors ${
                                            marcado
                                                ? 'bg-info-soft dark:bg-info-900/20'
                                                : 'hover:bg-mono-50 dark:hover:bg-mono-900'
                                        }`}
                                    >
                                        <td className="px-3 py-2">
                                            <input
                                                type="checkbox"
                                                checked={marcado}
                                                onChange={() => alternar(linha)}
                                                onClick={(evento) => evento.stopPropagation()}
                                                className="h-4 w-4"
                                            />
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            <Sku value={linha.sku} className="text-xs" />
                                        </td>
                                        <td
                                            className="w-full max-w-0 truncate px-3 py-2 text-mono-600 dark:text-mono-400"
                                            title={linha.titulo || ''}
                                        >
                                            {linha.titulo || '—'}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums">
                                            {FormatInt(linha.envios)}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-right">
                                            {linha.atrasados > 0 ? (
                                                <Badge variant="danger">{FormatInt(linha.atrasados)}</Badge>
                                            ) : (
                                                <span className="text-mono-400">0</span>
                                            )}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums">
                                            {FormatInt(linha.hoje)}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── Resultado ── */}
            <Modal
                isOpen={Boolean(resultado)}
                onClose={() => setResultado(null)}
                title={resultado?.tipo === 'lote' ? 'Lote criado' : 'OS de picking criada'}
                actions={
                    <Button variant="primary" onClick={() => setResultado(null)}>
                        Fechar
                    </Button>
                }
            >
                {resultado ? (
                    <div className="space-y-2 text-sm text-mono-700 dark:text-mono-300">
                        <p>
                            <Mono className="text-xs">{resultado.dados?.codigo}</Mono> com{' '}
                            <strong>{FormatInt(resultado.dados?.total ?? resultado.dados?.total_unidades ?? 0)}</strong>{' '}
                            envio(s).
                        </p>
                        {resultado.tipo === 'os' ? (
                            <>
                                <ul className="space-y-1 text-xs">
                                    {(resultado.dados?.plano ?? []).map((linha) => (
                                        <li key={linha.sku} className="flex flex-wrap items-center gap-2">
                                            <Mono>{linha.sku}</Mono>
                                            <span>
                                                {FormatInt(linha.reservado)}/{FormatInt(linha.solicitado)} reservado(s)
                                            </span>
                                            {linha.pallet_inteiro ? <Badge variant="info">pallet inteiro</Badge> : null}
                                            {linha.sem_estoque > 0 ? (
                                                <Badge variant="warning">sem estoque: {FormatInt(linha.sem_estoque)}</Badge>
                                            ) : null}
                                        </li>
                                    ))}
                                </ul>
                                <p className="text-xs text-mono-500">
                                    A OS nasceu <strong>no WMS</strong> com o estoque reservado (LPNs travados),
                                    destino <Mono className="text-[10px]">{resultado.dados?.destino}</Mono>.
                                    O picker a vê no <strong>Coletor</strong>; a mesa acompanha em Armazém →
                                    Ordens de Serviço. Envios vinculados:{' '}
                                    {FormatInt(resultado.dados?.envios_vinculados ?? 0)}.
                                </p>
                            </>
                        ) : (
                            <p className="text-xs text-mono-500">Aberto e destrancado, na aba Lotes. Modo: conjugada.</p>
                        )}
                        {resultado.dados?.sem_disponibilidade?.length ? (
                            <p className="text-xs text-warning-text">
                                Sem envio livre para: {resultado.dados.sem_disponibilidade.join(', ')}
                            </p>
                        ) : null}
                    </div>
                ) : null}
            </Modal>
        </div>
    );
};

export default CarrinhoGeracao;
